import { useState, useEffect } from "react";
import {
  FormGroup,
  FormControl,
  InputLabel,
  Input,
  Typography,
  styled,
  Button,
  FormHelperText,
  IconButton,
  InputAdornment,
  Autocomplete,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Global } from "@emotion/react";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";

const Container = styled(FormGroup)`
  padding: 5%;
  text-align: center;

  & > div {
    margin-top: 20px;
    color: white;
  }
`;

const Container1 = styled(InputLabel)`
  color: aliceblue;

  &.Mui-focused {
    color: var(--white) !important;
  }
`;

const Container2 = styled(Input)`
  color: aliceblue;
`;

const StyledButton = styled(Button)`
  background-color: var(--color-midnightblue);
  color: var(--white);
  margin-top: 20px;

  &:hover {
    background-color: var(--color-midnightblue);
  }
`;

const scrollbarStyles = `
::-webkit-scrollbar {
  width: 15px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background-color: var(--color-dimgray);
}

::-webkit-scrollbar-thumb:hover {
  background-color: var(--color-white-50);
}
`;

const PaymentStatusRadioGroup = ({
  key,
  value,
  onChange,
  error,
  options,
  disabled,
}) => {
  return (
    <FormControl error={error}>
      <Typography
        variant="subtitle1"
        style={{ textAlign: "left", color: "var(--white)" }}
      >
        {key}
      </Typography>
      <RadioGroup
        row
        name="paymentStatus"
        value={value}
        onChange={onChange}
        disabled={disabled}
      >
        {options.map((opt) => {
          return (
            <FormControlLabel
              value={opt}
              control={<Radio size="small" color="primary" />}
              label={opt}
            />
          );
        })}
      </RadioGroup>
      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
};

const FormComponent = ({
  formTitle,
  formConfig,
  buttonText,
  fieldDirection,
  fieldValue,
  handleAddOrUpdate,
  disableEdit,
  noCrossIcon,
}) => {
  const initialFormData = Object.fromEntries(
    formConfig.map((fieldConfig) => [fieldConfig.field.name, ""])
  );
  const navigate = useNavigate();
  const [formData, setFormData] = useState(initialFormData);
  const [formErrors, setFormErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (fieldValue) {
      setFormData(fieldValue);
      console.log("fieldValue", fieldValue);
      console.log(formData);
    }
  }, []);
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    const newErrors = { ...formErrors };
    const fieldConfig = formConfig.find(
      (fieldConfig) => fieldConfig.field.name === name
    );

    if (
      fieldConfig &&
      fieldConfig.field.error &&
      fieldConfig.field.error.condition
    ) {
      const isError = fieldConfig.field.error.condition(value);
      newErrors[name] = isError ? fieldConfig.field.error.message : null;
      setFormErrors(newErrors);
    }
  };
  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const validateFields = () => {
    const newErrors = {};
    formConfig.forEach((fieldConfig) => {
      const field = fieldConfig.field;
      if (field.error) {
        const isError = field.error.condition(formData[field.name]);
        if (isError === true) {
          newErrors[field.name] = field.error.message;
        }
      }
    });
    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleClose = () => {
    navigate(-1);
  };

  const renderField = (fieldConfig) => {
    const field = fieldConfig.field;
    const isViewOnly = disableEdit;

    switch (field.type) {
      case "num":
        return (
          <FormControl key={field.name} error={!!formErrors[field.name]}>
            <Container1>{field.title}</Container1>
            <Container2
              id={field.id}
              name={field.name}
              placeholder={field.placeholder}
              onChange={handleChange}
              required={field.required}
              autoComplete="off"
              disabled={isViewOnly}
              startAdornment={
                <InputAdornment position="start">$</InputAdornment>
              }
              value={formData[field.name]}
            />
            {formErrors[field.name] && (
              <FormHelperText>{formErrors[field.name]}</FormHelperText>
            )}
          </FormControl>
        );
      case "text":
      case "email":
      case "textArea":
        return (
          <FormControl key={field.name} error={!!formErrors[field.name]}>
            <Container1>{field.title}</Container1>
            <Container2
              {...(field.type === "textArea"
                ? { multiline: true, rows: 3 }
                : { type: "text" })}
              id={field.id}
              name={field.name}
              placeholder={field.placeholder}
              onChange={handleChange}
              required={field.required}
              value={formData[field.name]}
              autoComplete="off"
              disabled={isViewOnly}
            />
            {formErrors[field.name] && (
              <FormHelperText>{formErrors[field.name]}</FormHelperText>
            )}
          </FormControl>
        );

      case "password":
        return (
          <FormControl key={field.name} error={!!formErrors[field.name]}>
            <Container1>{field.title}</Container1>
            <Container2
              type={showPassword ? "text" : "password"}
              id={field.id}
              name={field.name}
              placeholder={field.placeholder}
              onChange={handleChange}
              required={field.required}
              value={formData[field.name]}
              disabled={isViewOnly}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    style={{ color: "var(--white)" }}
                    onClick={handleTogglePasswordVisibility}
                    edge="end"
                  >
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              }
            />
            {formErrors[field.name] && (
              <FormHelperText>{formErrors[field.name]}</FormHelperText>
            )}
          </FormControl>
        );

      case "dropdown":
        return (
          <FormControl key={field.name} error={!!formErrors[field.name]}>
            <Autocomplete
              id={field.id}
              options={field.options}
              getOptionLabel={(option) => option}
              onChange={(event, value) =>
                handleChange({ target: { name: field.name, value } })
              }
              value={formData[field.name]}
              disabled={isViewOnly}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={field.title}
                  placeholder={field.placeholder}
                  variant="standard"
                  error={!!formErrors[field.name]}
                />
              )}
            />
            {formErrors[field.name] && (
              <FormHelperText>{formErrors[field.name]}</FormHelperText>
            )}
          </FormControl>
        );

      case "radio":
        return (
          <PaymentStatusRadioGroup
            key={field.name}
            value={formData[field.name]}
            onChange={handleChange}
            error={formErrors[field.name]}
            options={field.options}
            disabled={isViewOnly}
          />
        );

      default:
        return null;
    }
  };

  const getFormData = () => {
    if (!validateFields()) {
      return;
    }
    handleAddOrUpdate(formData);
    setFormData(initialFormData);
  };

  const Tag = fieldDirection === "horizontal" ? "form" : Container;

  return (
    <Tag>
      {formTitle && (
        <div style={{ display: "flex", alignItems: "center" }}>
          <Typography
            variant="h4"
            width={"95%"}
            style={{ color: "var(--white)", fontWeight: 600 }}
          >
            {formTitle}
          </Typography>
          {noCrossIcon ? null : (
            <IconButton
              edge="end"
              onClick={handleClose}
              style={{
                borderRadius: "0.3vw",
                color: "var(--white)",
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </div>
      )}
      {formConfig.map((fieldConfig) => {
        return renderField(fieldConfig);
      })}
      {buttonText && (
        <StyledButton type="button" variant="contained" onClick={getFormData}>
          {buttonText}
        </StyledButton>
      )}
      <Global styles={scrollbarStyles} />
    </Tag>
  );
};

export default FormComponent;
